import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

import Table from "../../common/Table/table";
import { getAllClinic } from "../../../store/slices/clinicSlice";
import { getClientBill } from "../../../store/slices/clientBillSlice";

const ViewClientBill = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { clinicInfo, allClinicLists } = useSelector((state) => state.clinic);

  const { clientBill, loading, currentPage, totalPages, totalCount } =
    useSelector((state) => state.clientBill);

  const { token } = useSelector((state) => state.auth);

  const [reportFromData, setReportFromData] = useState({
    fromDate: "",
    toDate: "",
    paymentStatus: "ALL",
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [pageLimit, setPageLimit] = useState(10);
  const clinicId = clinicInfo?.[0]?.clinicId;

  // ==========================================
  // GET CLIENT LIST
  // ==========================================
  useEffect(() => {
    if (clinicId && token) {
      dispatch(
        getAllClinic({
          clinicId,
          loginRole: "Client",
          token,
        }),
      );
    }
  }, [dispatch, clinicId, token]);

  // ==========================================
  // FETCH TABLE DATA
  // ==========================================
  const fetchClientBills = useCallback(
    async (page = 1, limit = pageLimit) => {
      await dispatch(
        getClientBill({
          clinicId,
          token,
          fromDate: reportFromData.fromDate,
          toDate: reportFromData.toDate,
          clientId: selectedClient?.value,

          // Show both PAID and UNPAID
          status: "ALL",

          currentPage: page,
          limit,

          // Table should use pagination
          getAll: false,
        }),
      );
    },
    [
      dispatch,
      clinicId,
      token,
      reportFromData.fromDate,
      reportFromData.toDate,
      selectedClient,
      pageLimit,
    ],
  );

  // ==========================================
  // INITIAL LOAD
  // ==========================================
  useEffect(() => {
    if (clinicId && token) {
      fetchClientBills(1, pageLimit);
    }
  }, [clinicId, token, fetchClientBills, pageLimit]);

  // ==========================================
  // FILTER
  // ==========================================
  const handleFilter = useCallback(async () => {
    if (!selectedClient) {
      alert("Please select a client.");
      return;
    }

    if (!reportFromData.fromDate || !reportFromData.toDate) {
      alert("Please select From Date and To Date.");
      return;
    }

    if (reportFromData.fromDate > reportFromData.toDate) {
      alert("From Date cannot be greater than To Date.");
      return;
    }

    await fetchClientBills(1, pageLimit);
  }, [
    selectedClient,
    reportFromData.fromDate,
    reportFromData.toDate,
    fetchClientBills,
    pageLimit,
  ]);

  // ==========================================
  // CLEAR
  // ==========================================
  const handleClear = async () => {
    setReportFromData({
      fromDate: "",
      toDate: "",
      paymentStatus: "ALL",
    });

    setSelectedClient(null);

    await dispatch(
      getClientBill({
        clinicId,
        token,
        fromDate: "",
        toDate: "",
        clientId: undefined,
        status: "ALL",
        currentPage: 1,
        limit: pageLimit,
        getAll: false,
      }),
    );
  };

  // ==========================================
  // PRINT PDF
  // ==========================================
  const handlePrint = () => {
    // -----------------------------
    // VALIDATION
    // -----------------------------
    if (!selectedClient) {
      alert("Please select a client.");
      return;
    }

    if (!reportFromData.fromDate || !reportFromData.toDate) {
      alert("Please select From Date and To Date.");
      return;
    }

    if (reportFromData.fromDate > reportFromData.toDate) {
      alert("From Date cannot be greater than To Date.");
      return;
    }

    sessionStorage.setItem("clientBillPdfFilter", JSON.stringify({
      clientId: selectedClient.value,
      clientName: selectedClient.label,
      fromDate: reportFromData.fromDate,
      toDate: reportFromData.toDate,
    }));
    navigate("/clientBillPdf");
  };

  // ==========================================
  // FORMAT DATE
  // ==========================================
  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
      return "";
    }

    const day = String(d.getDate()).padStart(2, "0");

    const month = String(d.getMonth() + 1).padStart(2, "0");

    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  // ==========================================
  // PAGE CHANGE
  // ==========================================
  const handlePageChange = (page) => {
    fetchClientBills(page, pageLimit);
  };

  // ==========================================
  // LIMIT CHANGE
  // ==========================================
  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);

    fetchClientBills(1, newLimit);
  };

  // ==========================================
  // TABLE HEAD
  // ==========================================
  const tableHead = [
    {
      title: "Appt Date",
      key: "ApptDate",
    },
    {
      title: "Servicemode",
      key: "Servicemode",
    },
    {
      title: "Diagnosis",
      key: "Diagnosis",
    },
    {
      title: "Total",
      key: "Total",
    },
    {
      title: "Status",
      key: "Status",
    },
  ];

  // ==========================================
  // CLIENT OPTIONS
  // ==========================================
  const clientOptions =
    allClinicLists?.map((clinic) => ({
      label: clinic?.userId?.name,
      value: clinic?._id,
    })) || [];

  return (
    <>
      <div className="row d-flex align-items-center mb-2">
        {/* =========================
            CLIENT
        ========================= */}
        <div className="col-md-3">
          <div className="form-group form-focus focused">
            <label className="focus-label">Select Client Name</label>

            <Select
              options={clientOptions}
              value={selectedClient}
              onChange={setSelectedClient}
              placeholder="Select Client"
            />
          </div>
        </div>

        {/* =========================
            FROM DATE
        ========================= */}
        <div className="col-md-3">
          <div className="form-group form-focus focused">
            <label className="focus-label">From</label>

            <div className="cal-icon">
              <input
                className="form-control floating datetimepicker"
                type="date"
                value={reportFromData.fromDate}
                onChange={(e) =>
                  setReportFromData((prev) => ({
                    ...prev,
                    fromDate: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </div>

        {/* =========================
            TO DATE
        ========================= */}
        <div className="col-md-3">
          <div className="form-group form-focus focused">
            <label className="focus-label">To</label>

            <div className="cal-icon">
              <input
                className="form-control floating datetimepicker"
                type="date"
                value={reportFromData.toDate}
                onChange={(e) =>
                  setReportFromData((prev) => ({
                    ...prev,
                    toDate: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </div>

        {/* =========================
            BUTTONS
        ========================= */}
        <div className="col-md-3">
          <button
            className="btn btn-primary mt-3"
            onClick={handleFilter}
            disabled={loading}
          >
            Filter
          </button>

          <button className="btn btn-danger mt-3 ms-2" onClick={handleClear}>
            Clear
          </button>

          {/* PRINT BUTTON BESIDE CLEAR */}
          <button
            className="btn btn-success mt-3 ms-2"
            onClick={handlePrint}
            disabled={loading}
          >
            Print
          </button>
        </div>
      </div>

      {/* =========================
          NOTE
      ========================= */}
      <div className="row">
        <div className="col">
          <small className="text-muted">
            Note: Please select a client and date range before printing the
            report.
          </small>
        </div>
      </div>

      {/* =========================
          TABLE
      ========================= */}
      <Table
        tableHead={tableHead}
        showPaymentStatusTab={true}
        hideStatusTab={true}
        tableBody={
          clientBill &&
          clientBill.map((item) => {
            const tests = item?.appointmentId?.tests
              ?.map((test) => test.testName)
              .join(", ");

            const profiles = item?.appointmentId?.profiles
              ?.map((profile) => profile.profileName)
              .join(", ");

            return {
              ApptDate: item?.appointmentId?.appDate
                ? item.appointmentId.appDate.split("T")[0]
                : "",

              Servicemode: item?.appointmentId?.service?.serviceName || "",

              Diagnosis: [tests, profiles].filter(Boolean).join(", "),

              Total: item?.appointmentId?.totalAmount || 0,

              Status: item?.paymentStatus || "",
            };
          })
        }
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        limit={pageLimit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        showPagination={true}
        showLimitDropdown={true}
        paymentHeaderColor="paymentTabcolor"
        isLoading={loading}
      />
    </>
  );
};

export default ViewClientBill;
